import { useEffect, useState } from 'react';
import { showErrorToast, showWarningToast } from '@fff-web/fff-ui-shared/Toast';
import { useHCaptchaContext, usePaymentContext, usePricesContext } from '@fff-web/fff-payments';
import { identify, track, SKIP_TITLE_CASE } from '@fff-web/fff-analytics';
import { useSharedContext } from '../../pages/GTBCustomize/ContextProviders/SharedContext';
import { createIntroProductRegisterRequest, postIntroProductRegister } from '../../utils/Data/registerConnector';
import { isCallSuccessful } from '../../utils/Data/dataUtilities';
import { isResponseError, ResponseError } from '../../utils/Data/dataTypes';
import { usePublicShopContext } from '../../pages/GTBCustomize/ContextProviders/PublicShopContext';
import { redirectToShippingAddress } from '../../utils/redirectionUtilities';
import { signPactSafeIntroProductContracts } from '../../utils/Data/pactSafeConnector';
import { firePurchaseEvent } from '../../utils/Data/marketing';
import { SEASONAL_PLAN_CODE } from '../../utils';
import cookie from '../../utils/cookies';
import useGetIntroProductContracts from '../../hooks/useGetIntroProductContracts';
import { choicesToLineItems } from '../../utils/Data/marketingUtilitites';

const REGISTRATION_ERROR_MESSAGE = 'There was an error processing your request. Please contact customer support for further assistance.';
const SEGMENT_TRACK_NAME = 'Acquisition Completed';
const PURCHASE_EVENT = {
  intro: {
    event: 'Intro Product Subscribe',
    loggerMessage: 'INTRO PRODUCT - PURCHASE EVENT',
  },
  refill: {
    event: 'Public Subscription Purchase',
    loggerMessage: 'PUBLIC SUBSCRIPTION - PURCHASE EVENT',
  },
};

const SEGMENT_PRODUCT_TYPE = {
  intro: 'Intro Product',
  refill: 'Public Sub',
};
const COOKIE = {
  key: 'fff_registration_flow',
  value: 'intro-product',
};

const IntroProductRegister = () => {
  const {
    prospect,
    countries,
    promoCode,
    submitText,
    setLoading,
  } = useSharedContext();
  const { email } = prospect;
  const {
    sku, hasRefill, cartId, choices,
  } = usePublicShopContext();
  const { setHCaptchaToken, hCaptchaToken, setExecuteHCaptcha } = useHCaptchaContext();

  const {
    recurlyToken,
    setRecurlyToken,
    billingMethod,
    isAltPaymentOpen,
    recurlyError,
  } = usePaymentContext();

  const contracts = useGetIntroProductContracts();
  const [isValid, setIsValid] = useState(false);
  const { total } = usePricesContext();
  const purchaseEvent = PURCHASE_EVENT[hasRefill ? 'refill' : 'intro'];
  const resetRegistrationFlow = () => {
    setHCaptchaToken('');
    setExecuteHCaptcha(false);
    setIsValid(false);
  };

  const lineItems = choicesToLineItems(choices);

  useEffect(() => {
    if (!hCaptchaToken) {
      setLoading(false);
    }
  }, [hCaptchaToken]);

  // handle paypal + apple pay payment method cancel
  useEffect(() => {
    if (!isAltPaymentOpen) {
      setLoading(false);
    }
  }, [isAltPaymentOpen]);

  useEffect(() => {
    if (recurlyToken && hCaptchaToken) {
      setIsValid(true);
    }
  }, [recurlyToken, hCaptchaToken]);

  useEffect(() => {
    if (recurlyError) {
      showErrorToast(recurlyError.message);
    }
  }, [recurlyError]);

  useEffect(() => {
    if (isValid) {
      const registerRequest = createIntroProductRegisterRequest(
        recurlyToken as string,
        prospect,
        countries,
        billingMethod,
        sku,
        cartId,
      );

      postIntroProductRegister(registerRequest, hCaptchaToken as string)
        .then((response) => {
          const { status } = response;
          const statusMessage = response.status === 200 ? 'Success' : (response as ResponseError).error?.data.eventMessage;
          // for MVP we use hasRefill because we only support 1 item in cart
          const productType = hasRefill ? SEGMENT_PRODUCT_TYPE.refill : SEGMENT_PRODUCT_TYPE.intro;
          track({
            trackName: SEGMENT_TRACK_NAME,
            properties: {
              cta_name: submitText,
              type: productType,
              sku,
              status: statusMessage,
              payment_method: billingMethod,
              [SKIP_TITLE_CASE]: ['sku'],
            },
          });
          if (isCallSuccessful(status)) {
            const { event, loggerMessage } = purchaseEvent;
            const { subscriptionUuid, shopUserId } = response?.data;
            identify();
            firePurchaseEvent(
              event,
              loggerMessage,
              {
                couponCode: promoCode,
                email: prospect.email,
                planCode: hasRefill ? null : SEASONAL_PLAN_CODE,
                subscriptionUuid,
                userId: shopUserId,
                total,
                lineItems,
              },
            );
            cookie.set(COOKIE.key, COOKIE.value, 1);
            signPactSafeIntroProductContracts(email, contracts)
              .finally(() => redirectToShippingAddress());
            setLoading(false);
          } else {
            if (status === 500) {
              setLoading(false);
              showWarningToast(REGISTRATION_ERROR_MESSAGE);
              setRecurlyToken('');
              return resetRegistrationFlow();
            }
            if (isResponseError(response)) {
              setLoading(false);
              showErrorToast(response.error.data.message);
              setRecurlyToken('');
              return resetRegistrationFlow();
            }
          }

          return null;
        })
        .catch(() => {
          setLoading(false);
          showWarningToast(REGISTRATION_ERROR_MESSAGE);
          setRecurlyToken('');
          resetRegistrationFlow();
        });
    }
  }, [isValid]);

  return null;
};

export default IntroProductRegister;
